/* eslint-disable no-restricted-properties */

import { logRuntimeError } from 'Diagnostics/runtimeLogger';
import type { RuntimeConfigValue } from 'Models/uiConfig/requestTypes';

/**
 * Payload shape for `window.Shopify.analytics.publish(eventName, data)`.
 * Shopify Web Pixels accept any JSON-serializable bag of values; we model that with
 * `RuntimeConfigValue` so the constraint matches the rest of the runtime-config surface.
 * If a future cross-platform analytics-event type emerges, fold this in.
 */
type WebPixelEventPayload = Record<string, RuntimeConfigValue>;

enum ShopifyPageType {
  Home = 'home',
  Product = 'product',
  Collection = 'collection',
  Collections = 'collections',
  Vendors = 'vendors',
  Page = 'page',
  Cart = 'cart',
}

export function publishWebpixelEvent(eventName: string, data: WebPixelEventPayload): void {
  try {
    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish(eventName, data);
    }
  } catch (error) {
    // The Shopify Web Pixels surface is sandbox-loaded and can throw at any
    // time (script not yet ready, sandbox revoked, merchant override).
    // Swallowing the throw is intentional, but a silent catch made
    // misconfiguration invisible — surface the failure instead.
    logRuntimeError({
      source: 'shopify:publishWebpixelEvent',
      error,
      extra: { 'event.name': eventName },
    });
  }
}

export function getPageType(): ShopifyPageType | undefined {
  const convermaxPage = window.Convermax.Shopify?.pageType;
  if (convermaxPage) {
    switch (convermaxPage) {
      case 'index':
        return ShopifyPageType.Home;
      case 'collection':
        return window.Convermax.Shopify?.collectionVendor
          ? // vendor collection page (/collections/vendors?q=)
            ShopifyPageType.Vendors
          : ShopifyPageType.Collection;
      case 'list-collections':
        return ShopifyPageType.Collections;
      default:
        return toKnownPageType(convermaxPage);
    }
  }

  return toKnownPageType(window.ShopifyAnalytics?.meta?.page?.pageType);
}

function toKnownPageType(value: string | undefined): ShopifyPageType | undefined {
  if (!value) {
    return undefined;
  }
  const known = (Object.values(ShopifyPageType) as string[]).includes(value);
  return known ? (value as ShopifyPageType) : undefined;
}

/**
 * Several merchant themes inject Shopify globals as strings rather than
 * numbers (e.g. `meta.product.id = "1234"`). The static type says
 * `number | undefined`, but at runtime we must coerce or the downstream
 * `===` comparisons silently mismatch. Returns `undefined` for empty
 * strings, `NaN`, and non-finite values.
 */
function coerceId(value: number | string | undefined): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

export function getProductId(): number | undefined {
  return coerceId(window.Convermax.Shopify?.productId ?? window.ShopifyAnalytics?.meta?.product?.id);
}

export function getProductType(): string | undefined {
  return window.Convermax.Shopify?.productType ?? window.ShopifyAnalytics?.meta?.product?.type;
}

export function getVariantId(): number | undefined {
  // always get the updated variant id on a variant selection
  const urlVariantId = new URLSearchParams(window.location.search).get('variant');
  return (
    coerceId(urlVariantId ?? undefined) ??
    coerceId(
      window.Convermax.Shopify?.selectedVariantId ??
        window.ShopifyAnalytics?.meta?.product?.variants?.[0]?.id,
    )
  );
}

export function getCollectionId(): number | undefined {
  return coerceId(
    window.Convermax.Shopify?.collectionId ??
      (getPageType() === ShopifyPageType.Collection
        ? window.ShopifyAnalytics?.meta?.page?.resourceId
        : undefined),
  );
}

export function getPageId(): number | undefined {
  return coerceId(
    window.Convermax.Shopify?.pageId ??
      (getPageType() === ShopifyPageType.Page ? window.ShopifyAnalytics?.meta?.page?.resourceId : undefined),
  );
}

export function getCustomerId(): number | undefined {
  return coerceId(
    window.Convermax.Shopify?.customerId ??
      window.ShopifyAnalytics?.meta?.page?.customerId ??
      window.meta?.page?.customerId ??
      window.__st?.cid,
  );
}

export function getCustomerTags(): string[] | undefined {
  return window.Convermax.Shopify?.customerTags;
}

export function getCompanyId(): number | undefined {
  return window.Convermax.Shopify?.companyId;
}

export function getCompanyLocationId(): number | undefined {
  return window.Convermax.Shopify?.companyLocationId;
}

export function getCountry(): string | undefined {
  return window.Convermax.Shopify?.country ?? window.Shopify?.country;
}

export function getLocale(): string | undefined {
  return window.Convermax.Shopify?.locale ?? window.Shopify?.locale;
}

export function getCurrency(): string | undefined {
  return window.Convermax.Shopify?.currency ?? window.Shopify?.currency?.active;
}
export function getCurrencyRate(): string | undefined {
  // there is no way to obtain a rate using liquid
  return window.Shopify?.currency?.rate;
}

export function getRootRoute(): string {
  return (window.Convermax.Shopify?.rootRoute ?? window.Shopify?.routes?.root ?? '').replace(/\/+$/, '');
}

type CartUrls = {
  root?: string;
  add?: string;
  change?: string;
  clear?: string;
  update?: string;
};

export function getCartUrls(): CartUrls {
  const cmShopify = window.Convermax.Shopify;
  const rootRoute = getRootRoute();

  return {
    root: cmShopify?.cartUrl ?? `${rootRoute}/cart`,
    add: cmShopify?.cartAddUrl ?? `${rootRoute}/cart/add`,
    change: cmShopify?.cartChangeUrl ?? `${rootRoute}/cart/change`,
    clear: cmShopify?.cartClearUrl ?? `${rootRoute}/cart/clear`,
    update: cmShopify?.cartUpdateUrl ?? `${rootRoute}/cart/update`,
  };
}

export function getSearchPageUrl(): string {
  return window.Convermax.Shopify?.searchPageUrl ?? `${getRootRoute()}/pages/search`;
}

export function getCollectionsPageUrl(): string {
  return window.Convermax.Shopify?.collectionsUrl ?? `${getRootRoute()}/collections`;
}

/**
 * Optional-string narrowing helper for theme metadata fields. Anything
 * that isn't a non-empty string is dropped — `Object.assign` later
 * skips `undefined` properties, so callers see a clean shape.
 */
function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

export function getThemeInfo(): ThemeInfo | undefined {
  const theme = window.Shopify?.theme;
  if (!theme || typeof theme !== 'object') {
    return undefined;
  }
  // ThemeInfo's fields are all optional, but the wire format is at the
  // mercy of the storefront `<script>` block. Validate each field we
  // ever read so a string `id` (some merchants ship `"123"`) doesn't
  // mismatch a number-typed `getTrackedTheme()` comparison, and a
  // numeric `name` doesn't crash a `name.toLowerCase()` consumer.
  const raw = theme as Record<string, unknown>;
  const result: ThemeInfo = {};
  const id = coerceId(raw.id as number | string | undefined);
  if (id !== undefined) {
    result.id = id;
  }
  const name = optionalString(raw.name);
  if (name !== undefined) {
    result.name = name;
  }
  const role = optionalString(raw.role);
  if (role !== undefined) {
    result.role = role;
  }
  const schemaName = optionalString(raw.schema_name);
  if (schemaName !== undefined) {
    // eslint-disable-next-line camelcase
    result.schema_name = schemaName;
  }
  const schemaVersion = optionalString(raw.schema_version);
  if (schemaVersion !== undefined) {
    // eslint-disable-next-line camelcase
    result.schema_version = schemaVersion;
  }
  return result;
}

type ShopPayTerm = {
  apr: number;
  loan_type: string;
  installments_count: number;
};

type ShopPayFinancingPlan = {
  min_price: string;
  max_price: string;
  terms: ShopPayTerm[];
};

type ShopPayTerms = {
  min_price: string;
  max_price: string;
  financing_plans: ShopPayFinancingPlan[];
  installments_buyer_prequalification_enabled: boolean;
  seller_id: number;
};

export function getShopPayText(price: number, shopPayTerms?: ShopPayTerms): string | undefined {
  // You must reside within the United States (U.S.) and use a payment card with a United States billing address. All transactions must be carried out in USD.
  // https://help.shop.app/hc/en-us/articles/4408790562836-Shop-Pay-Installments-eligibility-and-requirements#:~:text=1.%20Eligibility%20criteria

  const country = getCountry();
  const currency = getCurrency();

  if (!shopPayTerms || country !== 'US' || currency !== 'USD') {
    return undefined;
  }

  const toNumber = (price: string) => Number(price.replace(',', '').replace('$', ''));

  if (price < toNumber(shopPayTerms.min_price)) {
    return `Pay over time for orders over <b>${shopPayTerms.min_price}</b> with`;
  } else if (price > toNumber(shopPayTerms.max_price)) {
    return `Split your purchase into installments for orders up to <b>${shopPayTerms.max_price}</b> with`;
  }

  const eligiblePlans = shopPayTerms.financing_plans.filter(
    (plan) => price >= toNumber(plan.min_price) && price <= toNumber(plan.max_price),
  );

  const splitPayTerm = eligiblePlans
    .find((plan) => plan.terms.some((term) => term.loan_type === 'split_pay'))
    ?.terms.find((term) => term.loan_type === 'split_pay');

  const interestTerms = eligiblePlans
    .find((plan) => plan.terms.some((term) => term.loan_type === 'interest'))
    ?.terms.filter((term) => term.loan_type === 'interest');

  const longestInterestTerm = interestTerms?.length
    ? interestTerms.reduce(
        (max, term) => (term.installments_count > max.installments_count ? term : max),
        interestTerms[0],
      )
    : null;

  let monthlyPaymentFormat = '';
  const priceFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

  if (longestInterestTerm) {
    const interestRate = longestInterestTerm.apr / 100;
    const paymentsCount = longestInterestTerm.installments_count;

    const monthlyPayment = (price * (interestRate / 12)) / (1 - (1 + interestRate / 12) ** -paymentsCount);
    monthlyPaymentFormat = priceFormatter.format(monthlyPayment);
  }

  if (splitPayTerm && longestInterestTerm) {
    return `${splitPayTerm.installments_count} interest-free installments, or from <b>${monthlyPaymentFormat}</b>/mo with`;
  } else if (longestInterestTerm) {
    return `From <b>${monthlyPaymentFormat}</b>/mo with`;
  } else if (splitPayTerm) {
    monthlyPaymentFormat = priceFormatter.format(price / splitPayTerm.installments_count);
    return `Pay in ${splitPayTerm.installments_count} interest-free installments of <b>${monthlyPaymentFormat}</b> with`;
  }
}

export const isInDesignMode = !!window.Convermax.Shopify?.isInDesignMode;

export type ConvermaxShopifyGlobal = {
  cartUrl?: string;
  cartAddUrl?: string;
  cartChangeUrl?: string;
  cartClearUrl?: string;
  cartUpdateUrl?: string;
  collectionId?: number;
  collectionsUrl?: string;
  collectionVendor?: string;
  country?: string;
  currency?: string;
  currencyRate?: string;
  customerId?: number;
  customerTags?: string[];
  companyId?: number;
  companyLocationId?: number;
  isInDesignMode?: boolean;
  locale?: string;
  pageType?: string;
  pageId?: number;
  productId?: number;
  productType?: string;
  rootRoute?: string;
  searchPageUrl?: string;
  selectedVariantId?: number;
} & ConvermaxShopifyHelpers;

// Kept hand-maintained because deriving this via `typeof shopifyHelpers` would
// pull the runtime closure — which transitively reads `window.Convermax.Shopify`
// back through `ConvermaxGlobal` — into the type graph and trigger a self-
// reference error on `ConvermaxShopifyGlobal`. Add/remove entries here when the
// helper set changes.
type ConvermaxShopifyHelpers = {
  getCartUrls: () => CartUrls;
  getCollectionId: () => number | undefined;
  getCollectionsPageUrl: () => string;
  getCountry: () => string | undefined;
  getCurrency: () => string | undefined;
  getCustomerId: () => number | undefined;
  getCustomerTags: () => string[] | undefined;
  getCompanyId: () => number | undefined;
  getCompanyLocationId: () => number | undefined;
  isInDesignMode: boolean;
  getLocale: () => string | undefined;
  getPageId: () => number | undefined;
  getPageType: () => ShopifyPageType | undefined;
  getProductId: () => number | undefined;
  getProductType: () => string | undefined;
  getRootRoute: () => string | undefined;
  getSearchPageUrl: () => string;
  getShopPayText: (price: number, terms?: ShopPayTerms) => string | undefined;
  getThemeInfo: () => ThemeInfo | undefined;
  getVariantId: () => number | undefined;
  publishWebpixelEvent: (eventName: string, data: WebPixelEventPayload) => void;
};

export type ThemeInfo = {
  id?: number;
  name?: string;
  role?: string;
  schema_name?: string;
  schema_version?: string;
};

export type Shopify = {
  country?: string;
  currency?: {
    active?: string;
    rate?: string;
  };
  locale?: string;
  routes?: {
    root?: string;
  };
  shop?: string;
  theme?: ThemeInfo;
  analytics?: {
    publish?: (eventName: string, data: WebPixelEventPayload) => void;
  };
};

export type ShopifyAnalytics = {
  meta?: {
    page?: {
      pageType?: string;
      resourceId?: number;
      customerId?: number;
    };
    product?: {
      id?: number;
      type?: string;
      variants?: Array<{ id?: number }>;
    };
  };
};

export type ShopifyMeta = {
  page?: {
    customerId?: number;
  };
};

export type ShopifyTools = {
  cid?: number;
};

const shopifyHelpers: ConvermaxShopifyHelpers = {
  getCartUrls,
  getCollectionsPageUrl,
  getCollectionId,
  getCompanyId,
  getCompanyLocationId,
  getCountry,
  getCurrency,
  getCustomerId,
  getCustomerTags,
  isInDesignMode,
  getLocale,
  getPageId,
  getPageType,
  getProductId,
  getProductType,
  getRootRoute,
  getSearchPageUrl,
  getShopPayText,
  getThemeInfo,
  getVariantId,
  publishWebpixelEvent,
};

export default shopifyHelpers;
